/**
 * The operators a gate's condition offers, by the type of the field it reads
 * (Feature 73, 4.2): only those the agent can make hold on that type, in
 * groups, Value, Items, Size, and Presence, where a type has more than one.
 * A field the picker cannot type offers all twenty. A string offers no
 * ordering, which the agent would make letter by letter (D9); a datetime
 * keeps it, since ISO times in one zone order correctly.
 */
import type { LinkageListOperator, LinkageOperator } from '@optum/api-client/types';

import type { ComboboxOption } from '../../ui/Combobox.js';
import { LIST_OPERATORS, LIST_OPERATOR_LABELS, isListOperator } from './lists.js';
import type { ReferenceType } from './references.js';

export const OPERATOR_LABELS: Record<LinkageOperator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  greater_than: 'greater than',
  greater_or_equal: 'greater or equal',
  less_than: 'less than',
  less_or_equal: 'less or equal',
  contains: 'contains',
  not_contains: 'does not contain',
  exists: 'exists',
  not_exists: 'does not exist',
  is_true: 'is true',
  is_false: 'is false',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  length_equals: 'length equals',
  length_not_equals: 'length does not equal',
  length_greater_than: 'length greater than',
  length_greater_or_equal: 'length greater or equal',
  length_less_than: 'length less than',
  length_less_or_equal: 'length less or equal',
};

/** The operators that test the field alone and take no value. */
export const VALUELESS: ReadonlySet<LinkageOperator> = new Set([
  'exists',
  'not_exists',
  'is_true',
  'is_false',
  'is_empty',
  'is_not_empty',
]);

/** The operators whose value is a whole number from 0 (D12). */
export const LENGTHS: readonly LinkageOperator[] = [
  'length_equals',
  'length_not_equals',
  'length_greater_than',
  'length_greater_or_equal',
  'length_less_than',
  'length_less_or_equal',
];

type Group = 'Value' | 'Items' | 'Size' | 'Presence';

const ORDERINGS: readonly LinkageOperator[] = [
  'greater_than',
  'greater_or_equal',
  'less_than',
  'less_or_equal',
];
const EQUALITY: readonly LinkageOperator[] = ['equals', 'not_equals'];
const MEMBERSHIP: readonly LinkageOperator[] = ['contains', 'not_contains'];
const TRUTH: readonly LinkageOperator[] = ['is_true', 'is_false'];
const SIZE: readonly LinkageOperator[] = ['is_empty', 'is_not_empty', ...LENGTHS];
const PRESENCE: readonly LinkageOperator[] = ['exists', 'not_exists'];

type Groups = readonly (readonly [Group, readonly LinkageOperator[]])[];

const NUMBERS: Groups = [
  ['Value', [...EQUALITY, ...ORDERINGS]],
  ['Presence', PRESENCE],
];

// Every type the picker knows, as the agent evaluates it (Feature 73, 4.2).
const BY_TYPE: Record<ReferenceType, Groups> = {
  integer: NUMBERS,
  number: NUMBERS,
  datetime: NUMBERS,
  string: [
    ['Value', [...EQUALITY, ...MEMBERSHIP]],
    ['Size', SIZE],
    ['Presence', PRESENCE],
  ],
  enum: [
    ['Value', EQUALITY],
    ['Presence', PRESENCE],
  ],
  boolean: [
    ['Value', TRUTH],
    ['Presence', PRESENCE],
  ],
  array: [
    ['Items', MEMBERSHIP],
    ['Size', SIZE],
    ['Presence', PRESENCE],
  ],
  object: [
    ['Size', ['is_empty', 'is_not_empty']],
    ['Presence', PRESENCE],
  ],
  any: [
    ['Value', [...EQUALITY, ...ORDERINGS, ...TRUTH]],
    ['Items', MEMBERSHIP],
    ['Size', SIZE],
    ['Presence', PRESENCE],
  ],
};

function groupsOf(type: ReferenceType | undefined): Groups {
  return BY_TYPE[type ?? 'any'];
}

/** The operators a field of this type offers, in order. */
export function operatorsFor(type: ReferenceType | undefined): LinkageOperator[] {
  return groupsOf(type).flatMap(([, ops]) => ops);
}

/** The operator list a rule row draws; a stored operator the type does not offer is flagged (D11). */
export function operatorOptions(
  type: ReferenceType | undefined,
  current: LinkageOperator,
): ComboboxOption[] {
  const groups = groupsOf(type);
  const grouped = groups.length > 1;
  const options: ComboboxOption[] = groups.flatMap(([group, ops]) =>
    ops.map((op) => ({ value: op, label: OPERATOR_LABELS[op], ...(grouped ? { group } : {}) })),
  );
  if (operatorsFor(type).includes(current)) return options;
  return [
    {
      value: current,
      label: OPERATOR_LABELS[current],
      aside: `not for ${type}`,
      asideTone: 'warn',
    },
    ...options,
  ];
}

/** The operator a rule keeps on another field: its own when the type offers it, else the type's first (D10). */
export function operatorOn(
  type: ReferenceType | undefined,
  current: LinkageOperator,
): LinkageOperator {
  const offered = operatorsFor(type);
  return offered.includes(current) ? current : (offered[0] ?? current);
}

/** Whether a field of this type offers the list tests: a list, or one the picker cannot type. */
export function offersListTests(type: ReferenceType | undefined): boolean {
  return type === undefined || type === 'array' || type === 'any';
}

/**
 * A gate condition's operator list: on a list, the three list tests first,
 * then the field's own groups (Feature 77 D27); a stored list test on a field
 * that is not a list stays, flagged, until it is changed.
 */
export function conditionOptions(
  type: ReferenceType | undefined,
  current: LinkageOperator | LinkageListOperator,
): ComboboxOption[] {
  const listed = isListOperator(current);
  const values = operatorOptions(type, listed ? (operatorsFor(type)[0] ?? 'exists') : current);
  if (!offersListTests(type)) {
    if (!listed) return values;
    const flagged: ComboboxOption = {
      value: current,
      label: LIST_OPERATOR_LABELS[current],
      aside: `not for ${type}`,
      asideTone: 'warn',
    };
    return [flagged, ...values];
  }
  const lists: ComboboxOption[] = LIST_OPERATORS.map((op) => ({
    value: op,
    label: LIST_OPERATOR_LABELS[op],
    group: 'List tests',
  }));
  return [...lists, ...values];
}

/** Whether a length's value is a whole number from 0 (D12). */
export function isLength(value: unknown): boolean {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}
